import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    limit, orderBy, query, where,
} from 'firebase/firestore';
import useOpportunities from 'data/firestore/agent/opportunity/useCollectionOnce';
import usePipelineStage from 'data/firestore/agent/pipeline/stage/use';
import getStageTypeColor from 'pages/Opportunities/helpers/getStageColor';
import { useOpportunityStageCache } from './OpportunityStageCacheContext';
import { StyledLatestOpportunityStageBadge } from './styled';

export interface CustomerLatestOpportunityStageBadgeProps {
    customerId: string;
}

/**
 * Latest opportunity stage for a customer (one list read + stage doc).
 * Results are stored on `OpportunityStageCacheProvider` for the lifetime
 * of the thread list so Virtuoso remounts do not refetch.
 */
const CustomerLatestOpportunityStageBadge = ({
    customerId,
}: CustomerLatestOpportunityStageBadgeProps) => {
    const { agentId = '' } = useParams<{ agentId: string }>();
    const { get, set } = useOpportunityStageCache();

    const resolved = get(agentId, customerId);
    const shouldFetchOpportunities = Boolean(agentId && customerId) && resolved === undefined;

    const {
        data: opportunities = [],
        isLoading: isLoadingOpportunities,
        error: opportunitiesError,
    } = useOpportunities(
        agentId,
        {
            listQueryFn: (ref) => query(
                ref,
                where('removed', '==', false),
                where('customer.id', '==', customerId),
                orderBy('createdAt', 'desc'),
                limit(1),
            ),
            disabled: !shouldFetchOpportunities,
            track: [agentId, customerId, shouldFetchOpportunities],
        },
    );

    /**
     * Do not treat the initial empty array as a completed fetch: on the first
     * effect pass `useCollectionOnce` has not set `isLoading` to true yet.
     */
    const opportunitiesFetchStartedRef = useRef(false);

    useEffect(() => {
        opportunitiesFetchStartedRef.current = false;
    }, [agentId, customerId]);

    useEffect(() => {
        if (isLoadingOpportunities) {
            opportunitiesFetchStartedRef.current = true;
        }
    }, [isLoadingOpportunities]);

    const latest = opportunities[0];
    const pipelineId = latest?.pipelineId ?? '';
    const stageId = latest?.stageId ?? '';

    const enableStageListener = Boolean(
        agentId
        && pipelineId
        && stageId
        && resolved === undefined,
    );

    const { data: stage, isLoading: isLoadingStage } = usePipelineStage(
        agentId,
        pipelineId,
        stageId,
        {
            disabled: !enableStageListener,
        },
    );

    useEffect(() => {
        if (resolved !== undefined) {
            return;
        }
        if (!agentId || !customerId) {
            return;
        }
        if (isLoadingOpportunities) {
            return;
        }
        if (!opportunitiesFetchStartedRef.current) {
            return;
        }
        if (opportunitiesError) {
            return;
        }
        if (opportunities.length === 0) {
            set(agentId, customerId, null);
        }
    }, [
        resolved,
        agentId,
        customerId,
        isLoadingOpportunities,
        opportunities.length,
        opportunitiesError,
        set,
    ]);

    useEffect(() => {
        if (resolved !== undefined) {
            return;
        }
        if (!agentId || !customerId || isLoadingStage) {
            return;
        }
        if (!latest?.id || !stage?.name) {
            return;
        }
        set(agentId, customerId, {
            label: stage.name,
            color: getStageTypeColor(stage.type),
            title: stage.name,
        });
    }, [
        resolved,
        agentId,
        customerId,
        isLoadingStage,
        latest,
        stage,
        set,
    ]);

    if (!customerId) {
        return null;
    }

    if (!resolved) {
        return null;
    }

    return (
        <StyledLatestOpportunityStageBadge
            label={resolved.label}
            color={resolved.color}
            title={resolved.title}
        />
    );
};

export default CustomerLatestOpportunityStageBadge;
